// SIGNALSTODO we may want to split this file up

import AcxDataGridStore from "components/UI/AcxDataGrid/AcxDataGridStore";
import BaseEntity from "models/BaseEntity";
import {
    SignalReportDatum,
    SignalTableDatum,
} from "models/Reporting/ReportDatum";
import type { ApplicationFilters } from "stores/ApplicationFilters/ApplicationFiltersStore";
import {
    SafetyEvent,
    SafetyEventAck,
    SafetyEventId,
    SafetyEventType,
    SafetyEventTypeOperator,
} from "stores/ApplicationFilters/Filters/SafetyEvent";
import { ReportAccessType } from "models/Reporting/ReportAccessControls";
import type { SkillGrouping } from "./BaseObjects/_DataEndpointConfig/Types";
import type { LayoutItem } from "./Dashboard/Layout/types";
import { SmallCardOwnInfo } from "./BaseObjects/SmallCard/SmallCardStore";
import {
    isDateGroupByOption,
    isNumericRangeGroupByOption,
    type NumericRangeMode,
    type GroupByConfiguration,
    type GroupByDraftConfiguration,
    type GroupByKind,
    type GroupByOptionValue,
} from "./Grouping/GroupByTypes";

export {
    GroupByOption,
    getGroupByKind,
    isCategoricalGroupByOption,
    isGroupByOption,
    isNumericRangeGroupByOption,
    nextBucketStart,
} from "./Grouping/GroupByTypes";
export type {
    CategoricalGroupByConfiguration,
    CategoricalGroupByOption,
    ContinuousGroupByOption,
    DateGroupByConfiguration,
    DateGroupByGrain,
    DateGroupByOption,
    GroupByConfiguration,
    GroupByDraftConfiguration,
    GroupByKind,
    GroupByOptionValue,
    NumericRangeGroupByConfiguration,
    NumericRangeGroupByDraft,
    NumericRangeGroupByOption,
    NumericRangeMode,
} from "./Grouping/GroupByTypes";

export const SignalsEndpoints = {
    // BY DASHBOARD
    eddyReports: {
        EEOverTimeID: "EddyEffectSignalsOverTime",
        EEByClassifierID: "EddyEffectSignalsByClassifier",
        EEByClassifiersOverTimeID: "EddyEffectByClassifiersOverTime",
        AverageScoreByQuestionID: "AverageScoreByQuestion",
        AverageScoreByQuestionAndEddyOverTimeID:
            "AverageScoreByQuestionAndEddyOverTime",
        EEPrevalenceByQuestionOverTimeID:
            "EddyEffectPrevalenceByQuestionResponseOverTime",
        EEPrevalenceByQuestionID: "EddyEffectPrevalenceByQuestion",
        ClassifierAndEddyID: "ClassifierPrevalenceAndEddyEffect",
    },
    trends: {
        ClassifierOverTimeId: "ClassifierPrevalenceOverTime",
        HipaaCompliancePrevalenceOverTimeId:
            "HipaaCompliancePrevalenceOverTime",
        AverageScoreByQuestionOverTimeID: "AverageScoreByQuestionOverTime",
        AverageScoreByModuleOverTimeID: "AverageScoreByModuleOverTime",
        SEPrevalenceOverTimeID: "SafetyEventPrevalenceOverTime",
        QualityScoreOverTimeID: "QualityScoreOverTime",
        // "Skill" is the client-facing term for a question. Scoped to one Quality module, unlike
        // eddyReports.AverageScoreByQuestionID (Eddy-grouped) or AverageScoreByQuestionOverTimeID.
        AverageScoreBySkillID: "AverageScoreBySkill",
    },
    legacyTopicReports: {
        // These persisted Topic-named endpoints read Fast Reporting Themes and require Topics/View.
        PrimaryThemesId: "TopicPrevalence",
        ThemesOverTimeId: "TopicPrevalenceOverTime",
        ThemeAndEEID: "TopicPrevalenceAndEddyEffect",
        TrendingTopicsID: "TrendingTopics",
        TopicsOnTheRiseID: "TopicsOnTheRise",
    },
    drivers: {
        TopTwentyThemesID: "TopTwentyThemes",
        ThemesAndTopDriversID: "ThemesAndTopDrivers",
        // The Primary Driver replacements for TrendingTopics and TopicsOnTheRise. They live here,
        // not in `legacyTopicReports`, because the group is what filterByPerms reads for
        // Drivers/View. Their predecessors stay in `legacyTopicReports` under Topics/View.
        TrendingDriversID: "TrendingDrivers",
        DriversOnTheRiseID: "DriversOnTheRise",
    },
    // Themes resolved through the Primary Driver. Registered as its own group rather than folded
    // into `legacyTopicReports` or `drivers`, so these Cards get their own
    // Drivers/View permission bucket in filterByPerms.
    themes: {
        ThemePrevalenceId: "ThemePrevalence",
        ThemePrevalenceOverTimeId: "ThemePrevalenceOverTime",
        ThemePrevalenceAndEEId: "ThemePrevalenceAndEddyEffect",
    },
    contacts: {
        ContactTypePrevalenceId: "ContactTypePrevalence",
        ContactTypePrevalenceOverTimeId: "ContactTypePrevalenceOverTime",
        ContactTypePrevalenceAndEEId: "ContactTypePrevalenceAndEddyEffect",
        EddyByContactTypeOverTimeId: "EddyEffectByContactTypeOverTime",
    },
    smallCards: {
        TotalConversationsId: "TotalConversations",
        AvgCallDurationId: "AvgCallDuration",
        ConversationsWithAnEddyId: "ConversationsWithAnEddy",
        AvgModuleScoreId: "AvgModuleScore",
    },
    additionalReports: {
        ConversationsOverTimeId: "ConversationsOverTime",
        CountByQuestionResponseId: "CountByQuestionResponse",
        ModelResultsCountId: "ModelResultsCount",
        ModelResultsOverTimeId: "ModelResultsOverTime",
        PublishedModelResultsOverTimeId: "PublishedModelResultsOverTime",
        PublishedModelResultsCountId: "PublishedModelResultsCount",
        GroupedModelResultsId: "GroupedModelResults",
        PublishedGroupedModelResultsId: "PublishedGroupedModelResults",
        ModuleResultsCountId: "ModuleResultsCount",
        JourneyStageCountId: "JourneyStageCount",
        JourneyStageAndEddyEffectId: "JourneyStageAndEddyEffect",
        CallDirectionId: "CallDirection",
        LillySafetyEventTypesId: "LillySafetyEventTypes",
        AdverseEventsModelPerformanceId: "AdverseEventsModelPerformance",
        SafetyEventsModelPerformanceId: "SafetyEventsModelPerformance",
        ProductComplaintsModelPerformanceId:
            "ProductComplaintsModelPerformance",
        SpecialSituationsModelPerformanceId:
            "SpecialSituationsModelPerformance",
        ExtractedConversationDataId: "ExtractedConversationData",
        TotalConversationsBarId: "TotalConversationsBar",
        TotalConversationsDonutId: "TotalConversationsDonut",
    },
    // BY TYPE (not all types present, but useful to grab all the tables for example)
    tables: {
        TrendingTopicsID: "TrendingTopics",
        TrendingDriversID: "TrendingDrivers",
        ExtractedConversationDataId: "ExtractedConversationData",
    },
    unique: {
        DriversOnTheRiseID: "DriversOnTheRise",
    },
} as const;

type EndpointCategories = typeof SignalsEndpoints;
type EndpointRecord = EndpointCategories[keyof EndpointCategories];

type EndpointValues<T> = T extends any ? T[keyof T] : never;

export type SignalDataEndpoint = EndpointValues<EndpointRecord>;

const EddyReportDataEndpoints = Object.values(
    SignalsEndpoints.eddyReports,
) as SignalDataEndpoint[];

const TrendsDataEndpoints = Object.values(
    SignalsEndpoints.trends,
) as SignalDataEndpoint[];

const LegacyTopicReportDataEndpoints = Object.values(
    SignalsEndpoints.legacyTopicReports,
) as SignalDataEndpoint[];

const DriversDataEndpoints = Object.values(
    SignalsEndpoints.drivers,
) as SignalDataEndpoint[];

const ThemesDataEndpoints = Object.values(
    SignalsEndpoints.themes,
) as SignalDataEndpoint[];

const ContactsDataEndpoints = Object.values(
    SignalsEndpoints.contacts,
) as SignalDataEndpoint[];

const AdditionalReportsDataEndpoints = Object.values(
    SignalsEndpoints.additionalReports,
) as SignalDataEndpoint[];

const TableDataEndpointValues = Object.values(
    SignalsEndpoints.tables,
) as SignalDataEndpoint[];

const UniqueDataEndpointValues: SignalDataEndpoint[] = [
    SignalsEndpoints.legacyTopicReports.TopicsOnTheRiseID,
    SignalsEndpoints.unique.DriversOnTheRiseID,
];

export const AllSignalDataEndpoints: SignalDataEndpoint[] = Array.from(
    new Set([
        ...EddyReportDataEndpoints,
        ...TrendsDataEndpoints,
        ...LegacyTopicReportDataEndpoints,
        ...DriversDataEndpoints,
        ...ThemesDataEndpoints,
        ...ContactsDataEndpoints,
        ...AdditionalReportsDataEndpoints,
        ...TableDataEndpointValues,
        ...UniqueDataEndpointValues,
    ]),
);

// Data endpoints that should be rendered as tables instead of charts.
export const TableDataEndpoints: SignalDataEndpoint[] = [
    ...TableDataEndpointValues,
];

export type SignalsRenderMode = "%" | "#";

export enum ChartType {
    Line,
    Table,
    VerticalBar,
    HorizontalBar,
    StackedVerticalBar,
    Donut,
    TreeMap,
}

export enum RenderModeType {
    Percent,
    Count,
}

export type OrderByDirection = "ascending" | "descending";

export type OrderByValues =
    | "volume"
    | "questionOrder"
    | "questionResponseOrder"
    | "alphabetical"
    // Date and numeric-range dimensions order by where a bucket sits in its own sequence rather than
    // by volume or label text. Compared by grouping identifier, which sorts correctly because a date
    // bucket is identified by its ISO start date and a range bucket by its ordinal.
    | "bucketOrder"
    | string[];

export type OrderRule = {
    orderByField: string | null;
    orderByDirection: OrderByDirection;
    orderByValues: OrderByValues;
};

export enum SignalsObjectType {
    Chart = 0,
    TextCard = 1,
    SmallCard = 2,
    MontageCard = 3,
    Table = 4,
    Unique = 5,
}

export enum SignalStatType {
    Number,
    Time,
    Percentage,
}

// This is currently unused
export enum DefinitionChartType {
    Line = 0,
    Table = 1,
    VerticalBar = 2,
    HorizontalBar = 3,
    StackedVerticalBar = 4,
}

// Temporary Definition Chart Type Value Mapping
export const DefinitionChartTypeValueMapping: Record<
    ChartType,
    SignalsChartType
> = {
    [ChartType.Line]: "Line",
    [ChartType.Table]: "Table",
    [ChartType.VerticalBar]: "VerticalBar",
    [ChartType.HorizontalBar]: "HorizontalBar",
    [ChartType.Donut]: "Donut",
    [ChartType.StackedVerticalBar]: "VerticalBar", // Have to do this temporarily because we rely on "VerticalBar" for chart rendering
    // To get this corrected, we must adjust our rendering to support "StackedVerticalBar" as a chart type
    [ChartType.TreeMap]: "TreeMap",
};

export enum DefinitionDataSelectionType {
    Classifiers = 0,
    Tags = 1,
    Questions = 2,
    Question = 3,
    Modules = 4,
    Topics = 5,
    SafetyEvent = 6,
    HIPPA = 7,
    Contact = 8,
    LLMClassifier = 9,
    PatientJourney = 10,
    Module = 11,
    CallDirection = 12,
    LLMClassifierResults = 13,
    None = 14,
    LLMClassifierExtractedMetadata = 15,
    ClassifierGroup = 16,
    // Mirrors Data.Models.Reporting.Signals.DataSelectionType. Themes = 17 must exist there too
    // before a card can persist it in SignalsBaseObjects.TypeOfDataSelectedInChart.
    Themes = 17,
}

// Temporary Definition Data Selection Type Value Mapping
export const DefinitionDataSelectionTypeMapping: Record<
    DefinitionDataSelectionType,
    ISignalsSelectionTypes
> = {
    [DefinitionDataSelectionType.Classifiers]: "Classifiers",
    [DefinitionDataSelectionType.Tags]: "Tags",
    [DefinitionDataSelectionType.Questions]: "Questions",
    [DefinitionDataSelectionType.Question]: "Question",
    [DefinitionDataSelectionType.Modules]: "Modules",
    [DefinitionDataSelectionType.Topics]: "Topics",
    [DefinitionDataSelectionType.Themes]: "Themes",
    [DefinitionDataSelectionType.SafetyEvent]: "Safety Event",
    [DefinitionDataSelectionType.HIPPA]: "HIPAA",
    [DefinitionDataSelectionType.Contact]: "Contact Type",
    [DefinitionDataSelectionType.LLMClassifier]: "LLM Classifier",
    [DefinitionDataSelectionType.PatientJourney]: undefined,
    [DefinitionDataSelectionType.Module]: "Module",
    [DefinitionDataSelectionType.CallDirection]: undefined,
    [DefinitionDataSelectionType.LLMClassifierResults]:
        "LLM Classifier Results",
    [DefinitionDataSelectionType.None]: undefined,
    [DefinitionDataSelectionType.LLMClassifierExtractedMetadata]:
        "LLM Classifier Extracted Metadata",
    [DefinitionDataSelectionType.ClassifierGroup]: "Classifier Group",
};

export interface SignalsPersistedObjectFilters {
    objectFilters: ApplicationFilters;
    objectFilterDateOverride: boolean;
    objectFilterDateTypeOverride: boolean;
    objectFilterCallDurationOverride?: boolean;
    objectFilterMessageCountOverride?: boolean;
}

export interface ISignalsBaseObject extends BaseEntity {
    title?: string;
    subtitle?: string;
    dataEndpoint?: SignalDataEndpoint;
    order: number;
    objectType: SignalsObjectType;
    vizOptions?: string | null;
    objectFilters?: string | null;
    typeOfDataSelectedInChart?: DefinitionDataSelectionType;
    orderRuleId?: string | null;
    orderRule?: OrderRule | null;
}

export interface ISignalsChartDefinition extends ISignalsBaseObject {
    typeOfChart: ChartType;
    typeOfDataSelectedInChart: DefinitionDataSelectionType;
    availableRenderModes: RenderModeType[];
    defaultRenderMode: RenderModeType;
    isPaginated: boolean;
    compareToPreviousModeIsAvailable: boolean;

    isChartPaginated: boolean;

    rawDataResponse?: any[];
    allRows?: any[];
    filteredRows?: any[]; // needed for paginated classifer charts to handled filtering rows based on selected classifier cat
    chartPageNumber: number;

    /**
     * Data formatted for google charts
     */
    data?: any[][];
    countData?: any[][];
    percentData?: any[][];

    idToRawRowsMap?: Map<string, SignalReportDatum[]>;

    xTitle: string;
    yTitle: string;

    chartType: SignalsChartType;
    isTemporal: boolean;
    isStackedBar: boolean;

    showComparePrevCheckbox: boolean;
    showPercentToggle: boolean;
    renderMode?: SignalsRenderMode;
    showDataGrid: boolean;
    dataGridStore: AcxDataGridStore;
    selectionType: ISignalsSelectionTypes;
    showSpotlight: boolean;
    showAnalyzeWithKBo: boolean;
    shouldReload: boolean;

    // used for HIPAA and SEPrev selection toggle without hitting endpoint
    seriesNames?: string[];
    visibleSeries?: string[];

    vizOptions: string;

    isEddyReport?: boolean;
    isTrendReport?: boolean;
    isTopicReport?: boolean;
    isContactReport?: boolean;
    isAdditionalReport?: boolean;
    isUserDashboardReport?: boolean;
    sqlQuery?: string;
}

export interface ISignalsTextCard extends ISignalsBaseObject {
    cardText: string;
}

export interface ISignalsMontageCard extends ISignalsBaseObject {
    montageId: string;
}

export interface ISignalsSmallCard extends ISignalsBaseObject {
    title: string;
    availableOptionObjects?: string; // Stringified array of AvailableOptionObjects
}

export type RenderedSignalsObject =
    | ISignalsChartDefinition
    | ISignalsTextCard
    | ISignalsMontageCard
    | ISignalsSmallCard
    | ISignalsBaseObject;

export interface ISignalsChartResponse {
    chartId: SignalDataEndpoint;
    data: SignalReportDatum[];
    xTitle: string;
    yTitle: string;
    sqlQuery: string | undefined;
    // True when the chart's classifier group was deleted (e.g. a pinned grouped-model-results chart whose
    // sub-group no longer exists). Drives the "group deleted" empty state.
    groupDeleted?: boolean;
    // True when a grouped-model-results chart was shared with a group in which the user can view none of
    // the models. Drives the "you don't have access to these models" empty state (instead of a generic error).
    groupAccessDenied?: boolean;
}

// Body for the attached `${dataEndpoint}/zoom` endpoint of a zoomable chart.
// Same envelope as the chart's base request, plus the full path from root.
// Path entries are `x_identifier` (or `x` as a fallback) values from prior
// zoom-in clicks; the backend resolves the path statelessly per call.
export interface ISignalsZoomRequest {
    zoomPath: string[];
}

// Response from a `${dataEndpoint}/zoom` call. `zoomPath` echoes the request so
// out-of-order responses can be discarded by the frontend store.
export interface ISignalsZoomResponse {
    zoomPath: string[];
    data: SignalReportDatum[];
    sqlQuery?: string;
}

// Metadata key used to mark a row as zoomable. Set by the backend on rows that
// have children; the point menu offers "View {nextLevelLabel}" only when this
// marker is present on the selected row.
export const HAS_CHILDREN_METADATA_KEY = "hasChildren";

export function rowHasChildren(
    metadata: { name: string; value: unknown }[] | undefined,
): boolean {
    return (
        metadata?.some(
            (m) => m.name === HAS_CHILDREN_METADATA_KEY && m.value === true,
        ) ?? false
    );
}

export interface ISignalsDrilldownResponse {
    count: number;
    data: {
        agentFirstName: string;
        agentLastName: string;
        arrivedOn: string;
        audioMetadataId: string;
        callDurationMillis: number;
        classifiers: IClassifiersFromDrilldown[];
        driverName?: string;
        driverIndex?: number;
        evalType: string;
        evaluationId: string | null;
        evaluationQbId: string | null;
        timestamp: string;
        hierarchy: string;
    }[];
    pageNumber: number;
    pageSize: number;
    totalCount: number;
    sqlQuery?: string;
}

export interface ISignalsTopicsDrilldownResponse {
    count: number;
    data: {
        name: string;
        /** The identifier the follow-on Conversation drilldown filters on. */
        topicNumber: number;
        volume: number;
        percent: number;
    }[];
    pageNumber: number;
    pageSize: number;
    totalCount: number;
    sqlQuery?: string;
}

/**
 * Result of breaking a Theme Data Point into its contributing Primary Drivers. The Topic
 * equivalent is {@link ISignalsTopicsDrilldownResponse}.
 */
export interface ISignalsDriversDrilldownResponse {
    count: number;
    data: {
        name: string;
        driverIndex: number;
        volume: number;
        percent: number;
    }[];
    pageNumber: number;
    pageSize: number;
    totalCount: number;
    sqlQuery?: string;
}

export interface ISignalsTrendingTopicsResponse {
    count: number;
    data: {
        percent: number;
        percentChange: number;
        themeName: string;
        topicName: string;
        volume: number;
        isNew: boolean;
        isToZero: boolean;
    }[];
    pageNumber: number;
    pageSize: number;
    totalCount: number;
    sqlQuery: string | undefined;
}

// --- Dynamic Table Response Types ---

export type ColumnFormatter =
    | "none"
    | "topicDrilldownCell"
    // Same cell behavior as topicDrilldownCell — click selects the row and opens the point menu.
    // Kept as its own name because the two resolve different selections: one a Topic, one a Driver.
    | "driverDrilldownCell"
    | "chip"
    | "trendIndicator"
    | "progressBar"
    | "link";

export interface SignalsTableColumn {
    field: string;
    headerName: string;
    type?: string; // "string" | "number" | "date"
    flex?: number;
    width?: number;
    sortable?: boolean;
    align?: "left" | "center" | "right";
    headerAlign?: "left" | "center" | "right";
    formatter?: ColumnFormatter;
    formatterOptions?: Record<string, unknown>;
    hide?: boolean;
}

export interface SignalsTablePagination {
    pageNumber: number;
    pageSize: number;
    totalCount: number;
}

export interface SignalsTableResponse {
    columns: SignalsTableColumn[];
    rows: SignalTableDatum[];
    pagination: SignalsTablePagination;
    sqlQuery?: string;
}

export interface ISignalsTopicsOnTheRiseResponse {
    data: ITopicOnTheRise[];
    sqlQuery: string | undefined;
}

export interface ISignalsDriversOnTheRiseResponse {
    data: IDriverOnTheRise[];
    sqlQuery: string | undefined;
}

export interface SignalsMontageResponse {
    audioName: string;
    createdOn: string;
    status: SignalsAudioStatus;
    duration: number;
}

export enum SignalsAudioStatus {
    HasAccess = 0,
    DoesNotHaveAccess = 1,
    Deleted = 2,
}

export interface IClassifiersFromDrilldown {
    audioMetadataId: string;
    classifierId: string;
    classifierName: string;
    classifierType: string;
    result: string;
    score: number;
}

export type ISignalsSelectionTypes =
    | "Classifiers"
    | "Question"
    | "Questions"
    | "Tags"
    | "Modules"
    | "Topics"
    | "Themes"
    | "HIPAA"
    | "Safety Event"
    | "Contact Type"
    | "LLM Classifier"
    | "LLM Classifier Results"
    | "Patient Journey"
    | "Module"
    | "Call Direction"
    | "LLM Classifier Extracted Metadata"
    | "Classifier Group"
    | undefined;

export type SignalsTemporalLabels =
    | "Daily"
    | "Weekly"
    | "Monthly"
    | "Quarterly";

export type SignalsChartType =
    | "Line"
    | "VerticalBar"
    | "StackedVerticalBar"
    | "HorizontalBar"
    | "Table"
    | "Donut"
    | "TreeMap";

interface AISummaryResultFailure {
    successful: false;
    message: string;
}

interface AISummaryResultSuccess {
    successful: true;
    summary: string;
    protectedDataBase64: string;
}

export type AISummaryResult = AISummaryResultFailure | AISummaryResultSuccess;
export interface AISummaryResponse {
    requestId: string;
    result: AISummaryResult;
}

export type SignalsGroupByCatalogItem = {
    // Option selected in vizOptions and sent to backend as GroupByOption.
    groupByOption: GroupByOptionValue;
    kind: GroupByKind;
    displayName: string;
    // Backend dimension aliases used when writing Grouping/GroupingIdentifier fields.
    // Categorical options only; continuous groupings derive their own aliases.
    groupingAlias?: string | null;
    groupingIdentifierAlias?: string | null;
    // Rule identifier used to resolve grouping-specific ordering.
    orderRuleIdentifier: string;
    // View permissions required to access this grouping dimension. Empty means no extra permission is required.
    requiredViewPermissions: string[];
    // Used by continuous ranges to determine buckets and units
    defaultCutPoints?: number[] | null;
    unit?: string | null;
    // The highest boundary this dimension accepts, in `unit`s, where its own filter has a ceiling.
    // Null where it has none. The editor rejects a boundary at or above it, matching the resolver.
    cutPointCeiling?: number | null;
    // Whether values between this dimension's units exist, which decides where its bucket
    // boundaries fall. Null for anything that is not a numeric range.
    numericRangeMode?: NumericRangeMode | null;
    // The top of this dimension's own scale, where its values cannot pass it: 100 for a percentage.
    // Null where the dimension runs open-ended, including where `cutPointCeiling` is set because a
    // filter stops there rather than because the values do.
    //
    // Both come off one definition, and they answer different questions. Every ceiling bounds the
    // boundaries a user may type, which is `cutPointCeiling`. Only a top the values cannot pass is
    // worth naming, and naming it is what closes the editor's last row at "95 to 100 %" and labels
    // the last bucket "95-100%" rather than "95+%".
    scaleTop?: number | null;
};

export type SignalsOrderRuleCatalogItem = {
    id: string;
    ruleIdentifier: string;
    orderByField: string | null;
    orderByDirection: OrderByDirection;
    orderByValues: OrderByValues;
};

export const SignalsGroupByOptionLabels: Record<GroupByOptionValue, string> = {
    Agent: "Agent",
    AgentManager: "Agent Manager",
    Hierarchy: "Hierarchy",
    ConversationLabeling: "Conversation Labeling",
    EvaluationType: "Evaluation Type",
    Language: "Language",
    MediaType: "Media Type",
    // The menu itself takes its title from the filter item and is differentiated by the status tag.
    // This map is the fallback the Orient-Chart-By toggle reads, and that label is a plain string,
    // so the deprecated one picks up a "(Deprecated)" marker at the point of use -- only for viewers
    // who can see that status. See ApplicationFilterItemsStore.withDeprecationMarker.
    PrimaryThemes: "Themes",
    Themes: "Themes",
    Outline: "Outline",
    PrimaryTopic: "Primary Topic",
    PrimaryDriver: "Primary Driver",
    PatientJourney: "Patient Journey",
    EddyEffect: "Eddy Signal",
    CallDirection: "Call Direction",
    ContactType: "Contact Type",
    Classifiers: "Classifiers",
    ModelResults: "Model Results",
    EndingSentiment: "Ending Sentiment",
    StartingSentiment: "Starting Sentiment",
    HipaaCompliance: "HIPAA Compliance",
    SafetyEvent: "Safety Event",
    ExtendedMetadata: "Extended Metadata",
    QuestionResult: "Question Result",
    Date: "Date",
    CallDuration: "Call Duration",
    MessageCount: "Message Count",
    ModuleScore: "Module Score",
};

type SignalsBaseRequest = {
    dateReference?: string;
    previousPeriodStartDate?: string;
    previousPeriodEndDate?: string;
    aggregateDataByTemporalGrain?: boolean;
    requestAiSummary?: boolean;
    requestId?: string;
    groupBy?: GroupByConfiguration;
    swapGroupingAndX?: boolean;
} & ApplicationFilters;

export type SignalsRequest<T = Record<string, unknown>> = T &
    SignalsBaseRequest;

export type SignalsFilters = Omit<
    SignalsBaseRequest,
    | "previousPeriodStartDate"
    | "previousPeriodEndDate"
    | "aggregateDataByTemporalGrain"
    | "requestAiSummary"
    | "requestId"
>;

export type ITopicOnTheRise = {
    topicName: string;
    topicNumber: number;
    themeNumber: number;
    percentChange: number;
    volume: number;
    isNew: boolean;
};

/**
 * A row on Drivers on the Rise. `driverIndex` is the stable identity
 * (FastReporting.PrimaryDriverIndex); `driverName` is display-only.
 *
 * Theme metadata is optional enrichment and is never rendered in the tile. Driver identity is
 * enough for View Data and View in Conversations when no Theme resolves.
 */
export type IDriverOnTheRise = {
    driverName: string;
    driverIndex: number;
    themeName: string;
    themeNumber: number;
    themeId: string;
    percentChange: number;
    volume: number;
    isNew: boolean;
};

export type SignalsDomain =
    | "eddy"
    | "trends"
    | "topics"
    | "drivers"
    | "contacts"
    | "dashboard"
    | "model-results";

export const getIsEddyReport = (dataEndpoint: SignalDataEndpoint) => {
    return EddyReportDataEndpoints.includes(dataEndpoint);
};

export const getIsTrendReport = (dataEndpoint: SignalDataEndpoint) => {
    return TrendsDataEndpoints.includes(dataEndpoint);
};

export const getIsTopicReport = (dataEndpoint: SignalDataEndpoint) => {
    return LegacyTopicReportDataEndpoints.includes(dataEndpoint);
};

export const getIsDriverReport = (dataEndpoint: SignalDataEndpoint) => {
    return DriversDataEndpoints.includes(dataEndpoint);
};

export const getIsThemeReport = (dataEndpoint: SignalDataEndpoint) => {
    return ThemesDataEndpoints.includes(dataEndpoint);
};

export const getIsContactReport = (dataEndpoint: SignalDataEndpoint) => {
    return ContactsDataEndpoints.includes(dataEndpoint);
};

export const getIsAdditionalReport = (dataEndpoint: SignalDataEndpoint) => {
    return AdditionalReportsDataEndpoints.includes(dataEndpoint);
};

export const getSafetyEventFromString = (
    value: string | null,
): SafetyEvent | undefined => {
    if (value === null) return;

    const baseSafetyEvent: SafetyEvent = {
        identification: null,
        typeOperator: SafetyEventTypeOperator.Or,
        acknowledgments: [],
        types: [],
    };

    switch (value) {
        case "Identified":
            return {
                ...baseSafetyEvent,
                identification: SafetyEventId.Identified,
            };
        case "Not Identified":
            return {
                ...baseSafetyEvent,
                identification: SafetyEventId.NotIdentified,
            };
        case "Acknowledged":
            return {
                ...baseSafetyEvent,
                acknowledgments: [SafetyEventAck.Acknowledged],
            };
        case "Not Acknowledged":
            return {
                ...baseSafetyEvent,
                acknowledgments: [SafetyEventAck.NotAcknowledged],
            };
        case "Adverse Event":
            return {
                ...baseSafetyEvent,
                types: [SafetyEventType.AdverseEvent],
            };
        case "Not Adverse Event":
            return {
                ...baseSafetyEvent,
                types: [SafetyEventType.NotAdverseEvent],
            };
        case "Product Quality Complaint":
            return {
                ...baseSafetyEvent,
                types: [SafetyEventType.ProductQualityComplaint],
            };
        case "Special Situation":
            return {
                ...baseSafetyEvent,
                types: [SafetyEventType.SpecialSituation],
            };

        default:
            return;
    }
};

// This refers to "themes" as topics and is the legacy themes via topic.
// Returned by `topics/trending`, which reads ThemeViaTopicsFastReporting*.
export interface SignalsTopics {
    id: string;
    topicLabel: string;
    estimatedNumberOfRecords?: number;
    estimatedPercentageOfTotal?: number;
    displayName?: string;
}

/**
 * A Theme catalog entry resolved through the Primary Driver, returned by `themes/trending`.
 * Sibling of {@link SignalsTopics}, which is the legacy Fast Reporting Theme catalog.
 */
export interface SignalsThemes {
    id: string;
    themeLabel: string;
    estimatedNumberOfRecords?: number;
    estimatedPercentageOfTotal?: number;
    displayName?: string;
}

export interface ISignalsDashboard {
    id: string;
    signalsObjects: RenderedSignalsObject[];
    signalsChartCount: number;
    title: string;
    description: string;
    userVisibility?: ReportAccessType; // We use ReportAccessType as the type for UserVisibility
    tenantVisibility?: IDashboardTenantVisibility;
    userId?: string;
    logoName: string;
    /**
     * To get the actual saved filter, you can find it in
     * ApplicationFiltersStore.savedFilters array
     */
    pinnedApplicationFilterId?: string;
    signalsDashboardSubscriptionId?: string;
    createdOn: any;
    dashboardType: SignalsDashboardType;
    /**
     * Every card's place on the grid, including cards hidden from this user:
     * an item without a matching card in `signalsObjects` is drawn as a
     * placeholder.
     */
    layout?: LayoutItem[];
    layoutSchemaVersion?: number;
}

export const SIGNALS_DASHBOARD_DESCRIPTION_MAX_LENGTH = 1000;

// All the valid dashboard types
export enum SignalsDashboardType {
    Custom = 0,
    ThemesAndTopics = 1,
    Eddy = 2,
    Trends = 3,
    ContactTypes = 4,
    ModelResults = 5,
    Hidden = 6,
    ThemesAndDrivers = 7,
}

// The types which are considered "standard" dashboards (everything but Custom pretty much)
export const StandardDashboardTypes = [
    SignalsDashboardType.Eddy,
    SignalsDashboardType.ContactTypes,
    SignalsDashboardType.ModelResults,
    SignalsDashboardType.ThemesAndTopics,
    SignalsDashboardType.ThemesAndDrivers,
    SignalsDashboardType.Trends,
    SignalsDashboardType.Hidden,
];

// Maps a dashboard type to its route segment
export const DashboardTypeRoutes = new Map<SignalsDashboardType, string>([
    [SignalsDashboardType.Eddy, "eddy"],
    [SignalsDashboardType.ThemesAndTopics, "topics"],
    [SignalsDashboardType.ThemesAndDrivers, "drivers"],
    [SignalsDashboardType.Trends, "trends"],
    [SignalsDashboardType.ContactTypes, "contacts"],
    [SignalsDashboardType.ModelResults, "model-results"],
    [SignalsDashboardType.Custom, "dashboard"],
    [SignalsDashboardType.Hidden, "hidden"],
]);

// Maps a dashboard type to the title of its standard home dashboard. Custom dashboards do not have a standard home
// E.g., Eddy -> "Eddy Reports"
export const StandardHomes = new Map<SignalsDashboardType, string>([
    [SignalsDashboardType.Eddy, "Eddy Reports"],
    [SignalsDashboardType.ThemesAndTopics, "Themes and Topics"],
    [SignalsDashboardType.ThemesAndDrivers, "Themes and Drivers"],
    [SignalsDashboardType.Trends, "Trends Highlights"],
    [SignalsDashboardType.ContactTypes, "Contact Types"],
    [SignalsDashboardType.ModelResults, "Model Results"],
]);

export enum IDashboardTenantVisibility {
    Restricted = 0,
    PubliclyViewable = 1,
    PubliclyEditable = 2,
}

export type IDashboardUserPermission = {
    userId: string;
    accessType: ReportAccessType;
};

export const SMALL_CARDS_DASHBOARD = "Small Cards";
export const ADDITIONAL_CHARTS_DASHBOARD = "Additional Reports";

export interface SmallCardCache {
    [key: string]: SmallCardOwnInfo | undefined;
}

export const viewInConvosDataEndpoints = [
    SignalsEndpoints.eddyReports.EEOverTimeID,
    SignalsEndpoints.eddyReports.ClassifierAndEddyID,
    SignalsEndpoints.eddyReports.EEByClassifierID,
    SignalsEndpoints.eddyReports.EEByClassifiersOverTimeID,
    SignalsEndpoints.legacyTopicReports.PrimaryThemesId,
    SignalsEndpoints.legacyTopicReports.ThemeAndEEID,
    SignalsEndpoints.legacyTopicReports.ThemesOverTimeId,
    SignalsEndpoints.legacyTopicReports.TrendingTopicsID,
    SignalsEndpoints.drivers.TopTwentyThemesID,
    SignalsEndpoints.drivers.ThemesAndTopDriversID,
    SignalsEndpoints.themes.ThemePrevalenceId,
    SignalsEndpoints.themes.ThemePrevalenceAndEEId,
    SignalsEndpoints.themes.ThemePrevalenceOverTimeId,
    SignalsEndpoints.trends.ClassifierOverTimeId,
    SignalsEndpoints.trends.HipaaCompliancePrevalenceOverTimeId,
    SignalsEndpoints.trends.SEPrevalenceOverTimeID,
    SignalsEndpoints.contacts.ContactTypePrevalenceId,
    SignalsEndpoints.contacts.ContactTypePrevalenceOverTimeId,
    SignalsEndpoints.contacts.ContactTypePrevalenceAndEEId,
    SignalsEndpoints.contacts.EddyByContactTypeOverTimeId,
    SignalsEndpoints.additionalReports.ModelResultsCountId,
    SignalsEndpoints.additionalReports.ModelResultsOverTimeId,
    SignalsEndpoints.additionalReports.JourneyStageAndEddyEffectId,
    SignalsEndpoints.additionalReports.JourneyStageCountId,
    SignalsEndpoints.additionalReports.ConversationsOverTimeId,
    SignalsEndpoints.additionalReports.CallDirectionId,
] as SignalDataEndpoint[];

export function canViewInConvos(dataEndpoint: SignalDataEndpoint): boolean {
    // We need to do these separately since PublishedModelResults ids have dynamic parts
    if (
        dataEndpoint.includes(
            SignalsEndpoints.additionalReports.PublishedModelResultsCountId,
        ) ||
        dataEndpoint.includes(
            SignalsEndpoints.additionalReports.PublishedModelResultsOverTimeId,
        )
    ) {
        return true;
    }
    return viewInConvosDataEndpoints.includes(dataEndpoint);
}

export type SignalsParsedVizOptions = {
    defaultSelectedIds?: string[];
    /**
     * Typed as the draft shape because the configuration drawer writes its working copy here while
     * the user is still editing. A saved chart only ever holds a complete `GroupByConfiguration` --
     * Save is blocked otherwise -- and request-time readers narrow to that through
     * `normalizeGroupByConfiguration`.
     */
    groupBy?: GroupByDraftConfiguration;
    /**
     * Grouping as it was stored before the canonical `groupBy` shape. Read-only: charts saved
     * before V3 still carry these, the readers in `Grouping/` translate them, and saving a chart
     * clears them. Nothing should write them.
     */
    groupByOption?: GroupByOptionValue;
    groupByIds?: string[];
    groupByLlmClassifierId?: string;
    groupByExtendedMetadataKey?: string;
    swapGroupingAndX?: boolean;
    selectedClassifierCategoryId?: string;
    /** Charts with a skill grouping selector: individual skills or skill groups. */
    skillGrouping?: SkillGrouping;
    colors?: string[]; // used for module results charts, might want to refactor it to a frontend solution
    scaleType?: "log" | "mirrorLog";
    // TreeMap (and any future zoom-capable chart): plural label per zoom depth.
    // levelLabels[0] is the top level; levelLabels[depth] is the level the user
    // reaches by zooming in from depth-1. Used by the point menu to build the
    // "View {label}" item, by breadcrumbs, and by header templates.
    levelLabels?: string[];
    // TreeMap-only: per-depth header text supporting `${N}` placeholders that
    // reference the zoom-path entry at depth N. Falls back to levelLabels[depth]
    // and then the current node's display name when missing or out of range.
    headerTemplates?: string[];
};

export const getDefaultSwapGroupingAndX = ({
    groupByOption,
    endpoint,
    isTemporal,
}: {
    groupByOption?: GroupByOptionValue;
    endpoint?: SignalDataEndpoint;
    isTemporal: boolean;
}): boolean => {
    if (isTemporal) return false;
    // In any of these cases below we want to use "true" for the swapGroupingAndX value by default. Otherwise it'll be false. If its a temporal chart it gets caught above and is ALWAYS false.
    return (
        isContinuousGroupByOption(groupByOption) ||
        groupByOption === "Agent" ||
        groupByOption === "AgentManager" ||
        endpoint ===
            SignalsEndpoints.additionalReports.TotalConversationsBarId ||
        endpoint ===
            SignalsEndpoints.additionalReports.TotalConversationsDonutId
    );
};

/**
 * Whether the option is a continuous dimension, meaning a date or a numeric range.
 *
 * Continuous dimensions default onto the x-axis rather than into the series, because their buckets
 * form an ordered run: a date-grouped chart reads as one bar per interval left to right, which is
 * only true when the buckets are the axis. Keyed on kind rather than on `Date` by name so the
 * numeric range dimensions inherit the same default when they land.
 */
const isContinuousGroupByOption = (
    groupByOption?: GroupByOptionValue,
): boolean =>
    isDateGroupByOption(groupByOption) ||
    isNumericRangeGroupByOption(groupByOption);

export enum SignalsEmailCadence {
    Immediate = 0,
    Daily = 1, // not implemented
    Weekly = 2,
    Monthly = 3,
}

export interface ExportableField {
    name: string;
    label: string;
}

export enum ExportableFieldCategory {
    Conversation,
    MachineLearningModel,
    ExtendedMetadata,
}

export function getExportableFieldCategoryString(
    category: ExportableFieldCategory,
) {
    switch (category) {
        case ExportableFieldCategory.Conversation:
            return "Conversation";
        case ExportableFieldCategory.MachineLearningModel:
            return "ML Models";
        case ExportableFieldCategory.ExtendedMetadata:
            return "Extended Metadata";
    }
}

export type ExportableFieldsResponse = {
    category: ExportableFieldCategory;
    fields: ExportableField[];
}[];
